<template>
  <v-card flat>
    <v-card-text>
      <v-row>
        <v-col cols="12" class="d-flex justify-end">
          <v-btn
            v-if="canEditDecisions"
            :color="editDecisionsMode ? 'primary' : 'default'"
            @click="editDecisionsMode = !editDecisionsMode"
            small
          >
            <v-icon left
              >mdi
              {{ editDecisionsMode ? "mdi-pencil-off" : "mdi-pencil" }}</v-icon
            >
            {{
              editDecisionsMode
                ? "Lopeta päätösten kirjaus"
                : "Kirjaa päätökset"
            }}
          </v-btn>
        </v-col>
        <v-col cols="12">
          <template v-for="tehtava in tehtavat">
            <v-card
              :key="tehtava.id"
              class="mb-4"
              outlined
              :style="`border: 2px solid ${getTaskColor(
                tehtava.status
              )} !important;`"
            >
              <v-card-title class="subtitle-1 font-weight-bold">
                <span v-if="tehtava.orderNumber" class="mr-2"
                  >{{ tehtava.orderNumber }} §</span
                >
                {{ tehtava.title }}
                <v-spacer />
                <v-btn
                  v-if="editDecisionsMode"
                  icon
                  class="mr-2"
                  @click="$emit('toggle-task-status', tehtava)"
                  :color="tehtava.status === 'READY' ? 'success' : 'grey'"
                  :title="
                    tehtava.status === 'READY'
                      ? 'Palauta keskeneräiseksi'
                      : 'Merkitse valmiiksi'
                  "
                >
                  <v-icon>mdi-check-circle</v-icon>
                </v-btn>
                <tehtava-status-bagde :status="tehtava.status" />
              </v-card-title>
              <v-card-text>
                <div v-if="tehtava.description" class="mb-3">
                  <div class="subtitle-2 mb-1">Kuvaus</div>
                  <div style="white-space: pre-wrap">
                    {{ tehtava.description }}
                  </div>
                </div>
                <div v-if="tehtava.descriptionKokouskutsu" class="mb-3">
                  <div class="subtitle-2 mb-1">Kuvaus kokouskutsussa</div>
                  <div style="white-space: pre-wrap">
                    {{ tehtava.descriptionKokouskutsu }}
                  </div>
                </div>

                <div
                  v-if="tehtava.comments && tehtava.comments.length > 0"
                  class="mb-3"
                >
                  <div class="subtitle-2 mb-1">Kommentit</div>
                  <v-list dense class="grey lighten-5 rounded">
                    <v-list-item
                      v-for="comment in tehtava.comments"
                      :key="comment.id"
                    >
                      <v-list-item-content>
                        <v-list-item-title class="text-wrap">{{
                          comment.comment
                        }}</v-list-item-title>
                        <v-list-item-subtitle
                          >{{ comment.createdBy.user }} -
                          {{
                            $formatDateAndHourMinute(comment.createdAt)
                          }}</v-list-item-subtitle
                        >
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </div>

                <div
                  v-if="tehtava.files && tehtava.files.length > 0"
                  class="mb-3"
                >
                  <div class="subtitle-2 mb-1">Tiedostot</div>
                  <ilmoitus-files-table
                    :files="tehtava.files"
                    @open="$emit('open-file', $event)"
                  />
                </div>

                <div
                  v-if="
                    (!tehtava.comments || tehtava.comments.length === 0) &&
                    (!tehtava.files || tehtava.files.length === 0)
                  "
                  class="caption grey--text font-italic mt-2"
                >
                  Ei vastauksia tai tiedostoja.
                </div>

                <div class="mt-4">
                  <div v-if="editDecisionsMode">
                    <v-textarea
                      v-model="tehtava.decision"
                      label="Päätös"
                      outlined
                      auto-grow
                      rows="2"
                      hide-details="auto"
                      @input="triggerSaveDecision(tehtava)"
                    >
                      <template v-slot:append>
                        <v-progress-circular
                          v-if="tehtava._savingDecision"
                          indeterminate
                          color="primary"
                          size="20"
                          width="2"
                        ></v-progress-circular>
                        <v-icon
                          v-else-if="tehtava._decisionSaved"
                          color="success"
                          >mdi-check</v-icon
                        >
                      </template>
                    </v-textarea>
                  </div>
                  <div v-else-if="tehtava.decision">
                    <tehtava-decision-display
                      :decision="tehtava.decision"
                      :decision-modified-by="tehtava.decisionModifiedBy"
                      :decision-modified-at="tehtava.decisionModifiedAt"
                    />
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </template>
        </v-col>

        <v-col cols="12">
          <v-divider class="my-4" />
          <h3>Kokouksen tiedostot</h3>
        </v-col>
        <v-col cols="12">
          <file-upload ref="uploadFilesTab"></file-upload>
          <v-progress-linear v-if="loadingFiles" indeterminate />
          <ilmoitus-files-table
            :files="ilmoitus.files"
            @open="$emit('open-file', $event)"
            @delete="$emit('delete-file', $event)"
          />

          <div v-if="tiedostopankkiFiles.length > 0">
            <v-data-table
              :headers="tiedostopankkiHeaders"
              :items="tiedostopankkiFiles"
              :items-per-page="-1"
              hide-default-footer
              class="elevation-1 mt-2"
              @click:row="$emit('open-file', $event)"
            >
              <template v-slot:item.icon="{ item }">
                <v-progress-circular
                  v-if="item._opening"
                  indeterminate
                  size="24"
                  color="primary"
                ></v-progress-circular>
                <v-icon v-else>mdi {{ getFileIcon(item.filename) }}</v-icon>
              </template>
              <template v-slot:item.createdAt="{ item }">
                {{ $formatDateAndHourMinute(item.createdAt) }}
              </template>
            </v-data-table>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import IlmoitusFilesTable from "@/components/IlmoitusFilesTable.vue";
import TehtavaDecisionDisplay from "@/components/TehtavaDecisionDisplay.vue";
import TehtavaStatusBagde from "@/views/TehtavaStatusBagde.vue";
import FileUpload from "@/components/FileUpload";

export default {
  name: "IlmoitusHallitusVastaukset",
  components: {
    IlmoitusFilesTable,
    TehtavaDecisionDisplay,
    TehtavaStatusBagde,
    FileUpload,
  },
  props: {
    canEditDecisions: {
      type: Boolean,
      default: false,
    },
    tehtavat: {
      type: Array,
      default: () => [],
    },
    ilmoitus: {
      type: Object,
      required: true,
    },
    loadingFiles: {
      type: Boolean,
      default: false,
    },
    tiedostopankkiFiles: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      editDecisionsMode: false,
      tiedostopankkiHeaders: [
        { text: "", value: "icon", sortable: false, width: "50px" },
        { text: "Nimi", value: "filename" },
        { text: "Kansio", value: "_categoryName" },
        { text: "Lisätty", value: "createdAt" },
        { text: "Lisääjä", value: "createdBy.user" },
      ],
    };
  },
  methods: {
    getFiles() {
      return this.$refs.uploadFilesTab?.getFiles();
    },
    getTaskColor(status) {
      switch (status) {
        case "NOT_STARTED":
          return "#E57373"; // red lighten-2
        case "IN_PROGRESS":
          return "#FF9800"; // orange
        case "READY":
          return "#4CAF50"; // green
        default:
          return "#E0E0E0"; // grey lighten-2
      }
    },
    getFileIcon(filename) {
      if (!filename) return "mdi-file";
      const extension = filename.split(".").pop();
      const icons = {
        pdf: "mdi-file-document",
        jpg: "mdi-file-image",
        jpeg: "mdi-file-image",
        png: "mdi-file-image",
        gif: "mdi-file-image",
        bmp: "mdi-file-image",
        mp3: "mdi-file-music",
        wav: "mdi-file-music",
        mp4: "mdi-file-video",
        mov: "mdi-file-video",
        avi: "mdi-file-video",
        doc: "mdi-file-word",
        docx: "mdi-file-word",
        xls: "mdi-file-excel",
        xlsx: "mdi-file-excel",
        ppt: "mdi-file-powerpoint",
        pptx: "mdi-file-powerpoint",
      };
      return icons[extension.toLowerCase()] || "mdi-file";
    },
    triggerSaveDecision(tehtava) {
      tehtava._decisionSaved = false;
      if (tehtava.debouncedSave) {
        tehtava.debouncedSave(tehtava);
      }
    },
  },
};
</script>

<style scoped>
::v-deep tbody tr {
  cursor: pointer;
}
</style>
